import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import AuthLayout from "../components/auth/AuthLayout";
import OTPInput from "../components/auth/OTPInput";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function TwoFactor() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const challengeId = sessionStorage.getItem("compliche-2fa-challenge");

  useEffect(() => {
    if (!challengeId) nav("/login");
  }, [challengeId, nav]);

  const submit = async (codeOverride) => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/api/auth/two-factor/verify", {
        challenge_id: challengeId,
        code: useBackup ? backupCode : (codeOverride || code),
        use_backup_code: useBackup,
      });
      setUser(data.user);
      sessionStorage.removeItem("compliche-2fa-challenge");
      try {
        const { data: w } = await api.get("/api/firm/wizard");
        nav(w.completed ? "/app/dashboard" : "/onboarding");
      } catch {
        nav("/app/dashboard");
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Two-factor authentication"
      subtitle={useBackup ? "Enter one of your 10 backup codes" : "Enter the 6-digit code from your authenticator app"}
      testid="two-factor-page"
      footer={<Link to="/login" className="text-white/70 hover:text-white">← Back to sign in</Link>}
    >
      <div className="flex items-center gap-2 mb-6 p-3 rounded-md security-card">
        <ShieldCheck size={18} className="text-emerald2-dark flex-shrink-0" />
        <span className="t-body-sm">This extra step protects your firm's data.</span>
      </div>

      {err && (
        <div className="mb-4 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: "rgba(239,68,68,0.06)", color: "#991B1B", border: "1px solid #FECACA" }} data-testid="twofactor-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />{err}
        </div>
      )}

      {!useBackup ? (
        <>
          <OTPInput length={6} value={code} onChange={setCode} onComplete={(c) => submit(c)} disabled={loading} />
          <button onClick={() => submit()} disabled={loading || code.length !== 6} className="btn btn-primary btn-block mt-6" data-testid="twofactor-verify">
            {loading ? <><Loader2 size={16} className="animate-spin" />Verifying…</> : "Verify"}
          </button>
        </>
      ) : (
        <>
          <input
            type="text"
            className="input2 t-mono"
            placeholder="abcd1234ef"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            data-testid="backup-code-input"
          />
          <button onClick={() => submit()} disabled={loading || !backupCode} className="btn btn-primary btn-block mt-4" data-testid="backup-verify">
            {loading ? <><Loader2 size={16} className="animate-spin" />Verifying…</> : "Use backup code"}
          </button>
        </>
      )}

      <button
        type="button"
        onClick={() => { setUseBackup(!useBackup); setErr(""); }}
        className="block mx-auto mt-5 text-indigo2 t-body-sm font-medium hover:underline"
        data-testid="toggle-backup"
      >
        {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
      </button>
    </AuthLayout>
  );
}
